import * as React from 'react';
import { Column, Columns, Flex, FormCheckbox, FormInput, FormSelect, Option } from 'lqx-react';
import { createStyles, Divider, FormControlLabel, Grid, makeStyles, Switch, Typography } from '@material-ui/core';
import { MultiEmailInput, MultiEmailSelect } from '../../../../../common/emails';
import { InputGroup } from '../common';
import { CustomerNotificationConfig, EmailConfig, RelationshipModel, SupplierNotificationConfig } from 'api/relationship';
import { ParticipantRoles } from 'api';

const generateStartTimeSeries = () =>  ["00:00", ...Array.from({length: 23}, (_, i) => ( ('0' + (i + 1)).slice(-2) + ":" + "00"))];

export const CustomerNotificationsSection = ({ relationship }: { relationship: RelationshipModel }) => {    
    const classes = useStyles();   
    const configs: CustomerNotificationConfig = relationship.notificationConfig.customerConfig;
    React.useEffect(() => {
        if(!configs.isEnabled) {
            configs.set({ 
                invoiceConfig : { isEnabled : false }, 
                disputeConfig : { isEnabled : false }, 
                disputeResolvedConfig : { isEnabled: false }
            });
        }
        
    }, [configs.isEnabled]);

    return <Flex className= { classes.notificationContainer }>
        <InputGroup>        
            <HeaderConfigSection title={ParticipantRoles.Customer} configs={configs}/>
            <EmailConfigSection title="New Invoice Notification" emailConfig={configs.invoiceConfig} disabled={!configs.isEnabled} relationship={relationship}/>
            <Divider /> 
            <EmailConfigSection title="New Dispute Notification" emailConfig={configs.disputeConfig} disabled={!configs.isEnabled} relationship={relationship}/>
            <Divider /> 
            <EmailConfigSection title="Dispute Resolved Notification" emailConfig={configs.disputeResolvedConfig} disabled={!configs.isEnabled} relationship={relationship}/>
        </InputGroup>
    </Flex>;
}

export const SupplierNotificationsSection = ({ relationship }: { relationship: RelationshipModel }) => {    
    const classes = useStyles();   
    const configs: SupplierNotificationConfig = relationship.notificationConfig.supplierConfig;
    React.useEffect(() => {
        if(!configs.isEnabled) {
            configs.set({ 
                approveInvoiceConfig : { isEnabled : false }, 
                disputeConfig : { isEnabled : false }, 
                disputeResolvedConfig : { isEnabled: false },
                submitRemittanceAdviceConfig: { isEnabled : false }
            });
        }
        
    }, [configs.isEnabled]);

    return <Flex className= { classes.notificationContainer }>
        <InputGroup>        
            <HeaderConfigSection title={ParticipantRoles.Supplier} configs={configs}/>
            <EmailConfigSection title="Approve Invoice Notification" emailConfig={configs.approveInvoiceConfig} disabled={!configs.isEnabled} relationship={relationship}/>
            <Divider />
            <EmailConfigSection title="New Dispute Notification" emailConfig={configs.disputeConfig} disabled={!configs.isEnabled} relationship={relationship}/>
            <Divider />
            <EmailConfigSection title="Dispute Resolved Notification" emailConfig={configs.disputeResolvedConfig} disabled={!configs.isEnabled} relationship={relationship}/>
            <Divider />
            <EmailConfigSection title="Submit Remittance Advice Notification" emailConfig={configs.submitRemittanceAdviceConfig} disabled={!configs.isEnabled} relationship={relationship}/>        
        </InputGroup>
    </Flex>;
}

const HeaderConfigSection = ({title, configs}: {title: String, configs : CustomerNotificationConfig | SupplierNotificationConfig} ) => {
    const classes = useStyles();
    return <Grid container item xs={12}>
        <Typography 
            variant="h5" 
            component="h5" 
            className={classes.panelHeading}
        >
            { title }
        </Typography>
        <FormControlLabel
            className={classes.panelControls}
            control={
                <Switch 
                    {...configs.$.isEnabled.props}
                    color="primary"
                />
            }
            label={ configs.isEnabled ? "Yes" : "No"}
            labelPlacement="end"
        />
    </Grid>
}
const EmailConfigSection = ({title, emailConfig, disabled, relationship}: {title: String, emailConfig : EmailConfig, disabled: boolean, relationship: RelationshipModel} ) => {
    const classes = useStyles();
    const emailOptions = relationship && relationship.relationshipUsers && relationship.relationshipUsers.map(ru => ru.userName);
    return (<>
        <Grid container item xs={12} alignContent="center">
            <Typography className={classes.notificationSettingTitle}>
                { title }
            </Typography>
            <FormControlLabel
                className={classes.panelControls}
                control={
                    <Switch 
                        {...emailConfig.$.isEnabled.props}
                        color="primary"
                        disabled={disabled}
                    />
                }
                label={ emailConfig.isEnabled ? "Yes" : "No"}
            />
        </Grid>      
        <Flex row className={classes.emailSectionHeader}>
            Frequency
        </Flex>
        <Flex column  className={classes.frequencyDetails}>
            <Columns>
                <Column>
                    <FormCheckbox label="Real time notification" $checked={ emailConfig.$.isRealTime } />
                </Column>
            </Columns>
            <Columns>
                <Column>
                    <FormInput label="Hours" positive number $value={ emailConfig.$.hours } disabled={ emailConfig.isRealTime }  />
                </Column>
                <Column>
                    <FormInput label="Minutes" positive number $value={ emailConfig.$.minutes } disabled={ emailConfig.isRealTime } />
                </Column>
                <Column>
                    <FormSelect label="Start Time"  $value={emailConfig.$.startTime} disabled={ emailConfig.isRealTime }>
                        { generateStartTimeSeries().map(st => <Option key={st} value={ st }>{ st }</Option>) }
                    </FormSelect>
                </Column>
            </Columns>
        </Flex>
        <Flex column className={classes.emailSectionHeader}>           
            {
                emailOptions && emailOptions.length > 0 ? 
                <MultiEmailSelect $emails={emailConfig.$.emails as any} emailOptions={emailOptions}/>  
                : <MultiEmailInput $emails={emailConfig.$.emails as any}/>
            }          
        </Flex>   
    </>
    );
}

const useStyles = makeStyles(() =>
    createStyles({
        panelHeading: {
            margin: 'auto auto auto 0',
            fontSize: 18,
            fontWeight: 500,
            marginBottom: 10
        },

        panelControls: {
            margin: 'auto 10px auto 0'
        },
        notificationSettingTitle: {
            margin: 'auto 20px',
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 10
        },
        emailSectionHeader: {
            margin: 'auto auto auto 40px',
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 10
        },
        frequencyDetails: {
            margin: 'auto 60px'
        },
        notificationContainer: {
            marginBottom: 10
        }
    }),
);